import { useCallback, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import type { Reader } from '@/api/client';
import { useCellCommentShellOptional } from '@/features/project-cell-comments/cellCommentShellContext';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ReaderCatalogReaderTagsMenuCascadeRow } from '@/components/ReadersTable/ReaderCatalogReaderTagsMenuCascadeRow';
import { CatalogTagsTableCellDisplay } from '@/components/ReadersTable/catalogTags/CatalogTagsTableCellDisplay';
import { readerCatalogTableDisplayName } from '@/utils/readerCatalogTableDisplayName';
import '@/components/ProjectsTable/EditableCell.css';

export interface ReaderCatalogTagsCellProps {
  reader: Reader;
  disabled?: boolean;
  onEditTags?: () => void;
}

export function ReaderCatalogTagsCell({ reader, disabled = false, onEditTags }: ReaderCatalogTagsCellProps) {
  const cellCommentShell = useCellCommentShellOptional();
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const cellRef = useRef<HTMLDivElement>(null);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setContextMenuPos({ x: e.clientX, y: e.clientY });
      setContextMenuOpen(true);
    },
    [disabled]
  );

  const contextMenuContent = useMemo(() => {
    const commentItem = cellCommentShell?.addCommentMenuItem;
    return (
      <ul className="context-menu__list" role="menu">
        {onEditTags ? (
          <ReaderCatalogReaderTagsMenuCascadeRow
            reader={reader}
            disabled={!!disabled}
            onCloseRootMenu={() => setContextMenuOpen(false)}
            onOpenCatalogModal={onEditTags}
          />
        ) : null}
        {commentItem ? (
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__item"
              disabled={!!disabled}
              role="menuitem"
              onClick={() => {
                setContextMenuOpen(false);
                commentItem.onClick();
              }}
            >
              <span className="context-menu__iconCell" aria-hidden>
                {commentItem.icon}
              </span>
              <span className="context-menu__item-text">{commentItem.label}</span>
            </button>
          </li>
        ) : null}
      </ul>
    );
  }, [cellCommentShell, disabled, onEditTags, reader]);

  return (
    <div
      ref={cellRef}
      className={clsx('editable-cell', disabled && 'readers-table-catalog__editable-cell_disabled')}
      onContextMenu={handleContextMenu}
    >
      <div className="editable-cell__content">
        <CatalogTagsTableCellDisplay
          tags={reader.tags}
          disabled={disabled}
          popoverSubjectName={readerCatalogTableDisplayName(reader)}
        />
      </div>
      <ContextMenu isOpen={contextMenuOpen} onClose={() => setContextMenuOpen(false)} anchorPosition={contextMenuPos}>
        {contextMenuContent}
      </ContextMenu>
    </div>
  );
}
